import _ from 'lodash';
import numeral from 'numeral';

import utils from 'app/helpers/utils';

const OptionStyles = {
  BLUE: 'blue',
  GREEN: 'green',
  BOLD_GREEN: 'bold-green',
  RED: 'red',
  BOLD_RED: 'bold-red',
  GRAY: 'gray',
};

const optionOpts = [
  {value: OptionStyles.BLUE,       label: 'Blue'},
  {value: OptionStyles.GREEN,      label: 'Green'},
  {value: OptionStyles.BOLD_GREEN, label: 'Bold Green'},
  {value: OptionStyles.RED,        label: 'Red'},
  {value: OptionStyles.BOLD_RED,   label: 'Bold Red'},
  {value: OptionStyles.GRAY,       label: 'Gray'},
];

const ScalePresetTypes = {
  OPTS_SCORE_4: 'opts-score-4',
  OPTS_SCORE_5: 'opts-score-5',
  OPTS_VOTE_5: 'opts-vote-5',
  OPTS_VOTE_4: 'opts-vote-4',
  OPTS_PASS_FAIL: 'opts-pass-fail',
  OPTS_CUSTOM: 'opts-custom',
  RNG_100: 'rng-0-100',
  RNG_CUSTOM: 'rng-custom',
};

const scalePresets = {
  [ScalePresetTypes.OPTS_SCORE_4]: {
    title: '4pt Score',
    key: ScalePresetTypes.OPTS_SCORE_4,
    opts: [
      {label: 'Absent',    value: 0, style: OptionStyles.GRAY},
      {label: 'Poor',      value: 1, style: OptionStyles.BLUE},
      {label: 'Fair',      value: 2, style: OptionStyles.BLUE},
      {label: 'Good',      value: 3, style: OptionStyles.BLUE},
      {label: 'Great',     value: 4, style: OptionStyles.BLUE},
    ],
    range: null,
  },
  [ScalePresetTypes.OPTS_SCORE_5]: {
    title: '5pt Score',
    key: ScalePresetTypes.OPTS_SCORE_5,
    opts: [
      {label: 'Absent',    value: 0, style: OptionStyles.GRAY},
      {label: 'Poor',      value: 1, style: OptionStyles.BLUE},
      {label: 'Fair',      value: 2, style: OptionStyles.BLUE},
      {label: 'Good',      value: 3, style: OptionStyles.BLUE},
      {label: 'Great',     value: 4, style: OptionStyles.BLUE},
      {label: 'Excellent', value: 5, style: OptionStyles.BLUE},
    ],
    range: null,
  },
  [ScalePresetTypes.OPTS_VOTE_4]: {
    title: '4pt Vote',
    key: ScalePresetTypes.OPTS_VOTE_4,
    opts: [
      {label: 'Strong No',  value: 0, style: OptionStyles.BOLD_RED},
      {label: 'No',         value: 1, style: OptionStyles.RED},
      {label: 'Yes',        value: 2, style: OptionStyles.GREEN},
      {label: 'Strong Yes', value: 3, style: OptionStyles.BOLD_GREEN},
    ],
    range: null,
  },
  [ScalePresetTypes.OPTS_VOTE_5]: {
    title: '5pt Vote',
    key: ScalePresetTypes.OPTS_VOTE_5,
    opts: [
      {label: 'Strong No',  value: 0, style: OptionStyles.BOLD_RED},
      {label: 'No',         value: 1, style: OptionStyles.RED},
      {label: 'Neutral',    value: 2, style: OptionStyles.GRAY},
      {label: 'Yes',        value: 3, style: OptionStyles.GREEN},
      {label: 'Strong Yes', value: 4, style: OptionStyles.BOLD_GREEN},
    ],
    range: null,
  },
  [ScalePresetTypes.OPTS_PASS_FAIL]: {
    title: 'Pass/Fail',
    key: ScalePresetTypes.OPTS_PASS_FAIL,
    opts: [
      {label: 'Fail', value: 0, style: OptionStyles.BOLD_RED},
      {label: 'Pass', value: 1, style: OptionStyles.BOLD_GREEN},
    ],
    range: null,
  },
  [ScalePresetTypes.RNG_10]: {
    title: 'Range: 0-10',
    key: ScalePresetTypes.RNG_10,
    opts: null,
    range: {min: 0, max: 10},
  },
  [ScalePresetTypes.RNG_100]: {
    title: 'Range: 0-100',
    key: ScalePresetTypes.RNG_100,
    opts: null,
    range: {min: 0, max: 100},
  },
};

const scalePresetOpts = [
  ...Object.values(scalePresets).map(preset => ({label: preset.title, value: preset.key})),
  {label: 'Custom Options', value: ScalePresetTypes.OPTS_CUSTOM},
  {label: 'Custom Range', value: ScalePresetTypes.RNG_CUSTOM},
];

const NormTypes = {
  PERCENTAGE: 'percentage',
  GRAD_LABELS: 'grad-labels',
};

const normTypeObjs = {
  [NormTypes.PERCENTAGE]: {
    key: NormTypes.PERCENTAGE,
    label: 'Percentage',
  },
  [NormTypes.GRAD_LABELS]: {
    key: NormTypes.GRAD_LABELS,
    label: 'Labels',
  },
};

const normTypeOpts = Object.values(normTypeObjs).map((normObj) => {
  return {label: normObj.label, value: normObj.key};
});

const GradLabelPresetTypes = {
  LETTER_GRADE: 'letter-grade',
  PASS_FAIL: 'pass-fail',
  CUSTOM: 'custom',
};

const gradLabelPresets = {
  [GradLabelPresetTypes.LETTER_GRADE]: {
    key: GradLabelPresetTypes.LETTER_GRADE,
    label: 'Letter Grade',
    gradLbls: [
      {threshold: 90_000, label: 'A', style: OptionStyles.BOLD_GREEN},
      {threshold: 80_000, label: 'B', style: OptionStyles.GREEN},
      {threshold: 70_000, label: 'C', style: OptionStyles.BLUE},
      {threshold: 60_000, label: 'D', style: OptionStyles.RED},
      {threshold: 0,      label: 'F', style: OptionStyles.BOLD_RED},
    ],
  },
  [GradLabelPresetTypes.PASS_FAIL]: {
    key: GradLabelPresetTypes.PASS_FAIL,
    label: '80/20 Binary',
    gradLbls: [
      {threshold: 80_000, label: 'PASS', style: OptionStyles.BOLD_GREEN},
      {threshold: 0,      label: 'FAIL', style: OptionStyles.BOLD_RED},
    ],
  },
};
const defaultGradLabelPreset = gradLabelPresets[GradLabelPresetTypes.LETTER_GRADE];

const gradLabelPresetOpts = Object.values(gradLabelPresets).map((presetObj) => {
  return {label: presetObj.label, value: presetObj.key};
});
gradLabelPresetOpts.push({label: 'Custom', value: GradLabelPresetTypes.CUSTOM});

const findPresetKey = ({opts: dirtyOpts, range}) => {
  const opts = dirtyOpts ? dirtyOpts.map(o => _.pick(o, ['label', 'value', 'style'])) : null;
  const preset = Object.values(scalePresets).find((preset) => {
    if (opts && preset.opts && opts.length === preset.opts.length) {
      for (let i = 0; i < opts.length; i++) {
        if (!_.isEqual(opts[i], preset.opts[i])) return false;
      }
      return true;
    }
    if (range && preset.range) {
      if (range.min === preset.range.min && range.max === preset.range.max) return true;
    }
  });
  if (preset) return preset.key;
  if (opts) return ScalePresetTypes.OPTS_CUSTOM;
  if (range) return ScalePresetTypes.RNG_CUSTOM;
  return null;
};


const defaultOpts = scalePresets[ScalePresetTypes.OPTS_SCORE_4].opts;
const defaultRange = {min: 0, max: 100};
const cloneOpts = (opts) => {
  if (opts == null) return null;
  return opts.map((opt) => {
    const newOpt = {...opt, id: utils.clientId()};
    delete newOpt.critId;
    return newOpt;
  });
};
const makeCrit = (existingCrits=[], attrs={}, opts) => {
  const count = (existingCrits?.length || 0) + 1;
  const last = existingCrits ? existingCrits[existingCrits.length - 1] : null;
  const useOpts = opts || !last || !!last.opts || !last.range;
  let weightLeft = 100000;
  (existingCrits || []).forEach((crit) => {
    weightLeft -= crit.weight;
  });
  return {
    label: `Criterion #${count}`,
    weight: Math.max(weightLeft, 0),
    opts:  useOpts ? cloneOpts(opts || last?.opts || defaultOpts) : null,
    range: useOpts ? null : _.cloneDeep(last?.range || defaultRange),
    ...attrs,
    id: utils.clientId(),
  };
};

const cloneCrit = (crit) => {
  const opts = cloneOpts(crit.opts);
  const range = _.cloneDeep(crit.range);
  const label = `Copy of ${crit.label}`;
  const attrs = {...crit, opts, range, label};
  delete attrs.rubId;
  return makeCrit([], attrs);
};

const makeOpt = (existingOpts=[], attrs={}) => {
  const last = existingOpts ? existingOpts[existingOpts.length - 1] : null;
  return {
    label: `Option #${(existingOpts?.length || 0) + 1}`,
    style: (last?.style || OptionStyles.BLUE),
    value: (_.isFinite(last?.value) ? (last.value + 1) : 1),
    ...attrs,
    id: utils.clientId(),
  };
};

const makeGradLabel = (existingGradLbls=[], attrs={}) => {
  const last = existingGradLbls ? existingGradLbls[existingGradLbls.length - 2] : null;
  return {
    label: `Label #${(existingGradLbls?.length || 0) + 1}`,
    style: (last?.style || OptionStyles.BLUE),
    threshold: last ? Math.max(last.threshold - 10_000, 1_000) : 90_000,
    ...attrs,
    id: utils.clientId(),
  };
};

const makeRubric = (attrs={}) => {
  return {
    id: utils.clientId(),
    crits: [],
    normType: NormTypes.PERCENTAGE,
    ...attrs,
  };
};
const makeRubricPassFail = (attrs={}) => {
  const opts = scalePresets[ScalePresetTypes.OPTS_PASS_FAIL].opts;
  const crits = [makeCrit([], {label: 'Pass/Fail', weight: 100_000}, opts)];
  return makeRubric({crits});
};
const makeRubricVote = (attrs={}) => {
  const opts = scalePresets[ScalePresetTypes.OPTS_VOTE_5].opts;
  const crits = [makeCrit([], {label: 'Your Vote', weight: 100_000}, opts)];
  return makeRubric({crits});
};
const makeRubricScore = (attrs={}) => {
  const opts = scalePresets[ScalePresetTypes.OPTS_SCORE_4].opts;
  const crits = [makeCrit([], {label: 'Score', weight: 100_000}, opts)];
  return makeRubric({crits});
};
const makeRubricFinal = (attrs={}) => {
  const opts = [
    {label: 'Decline', value: 0, style: OptionStyles.BOLD_RED},
    {label: 'Approve', value: 1, style: OptionStyles.BOLD_GREEN},
  ];
  const crits = [makeCrit([], {label: 'Final Decision', weight: 100_000}, opts)];
  return makeRubric({crits});
};

const critAttrsFromPreset = (presetKey) => {
  const preset = scalePresets[presetKey];
  const range = preset?.range ? {...preset.range} : null;
  const opts = preset?.opts ? preset.opts.map(o => makeOpt([], {...o})) : null;
  return {range, opts};
};

const formatWeight = (weightVal, decPlaces=3) => {
  const decStr = (() => {
    if (!_.isFinite(decPlaces)) return '';
    if ((decPlaces <= 0) || decPlaces > 10) return '';
    let zeros = '0';
    while (zeros.length < decPlaces) zeros += '0';
    return `[.][${zeros}]`;
  })();
  const fmtStr = `0${decStr}%`;

  const weightPercent = (weightVal || 0) / 1000;
  return numeral(weightPercent / 100).format(fmtStr);
};

const areWeightsEqual = (crits) => {
  if (!crits?.length) return true;
  const target = 100_000 / crits.length;
  return !crits.find(({weight}) => {
    return Math.abs(weight - target) > 1;
  });
};

const getVldGradLbl = (gradLbl) => {
  if (!gradLbl) return null;
  const v = {};
  if (!(gradLbl.label || '').trim()) v.label = ['required'];
  if (_.isFinite(gradLbl.threshold)) {
    if (gradLbl.threshold < 0) v.threshold = ['min 1%'];
    if (gradLbl.threshold > 100_000) v.threshold = ['max 100%'];
  } else {
    v.threshold = ['required'];
  }
  if (!Object.values(OptionStyles).includes(gradLbl.style)) v.style = ['required'];
  return Object.keys(v).length ? v : null;
};

const getVldOpt = (opt) => {
  if (!opt) return null;
  const v = {};
  if (!(opt.label || '').trim()) v.label = ['required'];
  if (_.isFinite(opt.value)) {
    if (opt.value < 0) v.value = ['min 0'];
  } else {
    v.value = ['required'];
  }
  if (!Object.values(OptionStyles).includes(opt.style)) v.style = ['required'];
  return Object.keys(v).length ? v : null;
};

const getVldCrit = (crit) => {
  if (!crit) return null;
  const v = {};
  if (!(crit.label || '').trim()) v.label = ['required'];
  if (_.isFinite(crit.weight)) {
    if (crit.weight < 0) v.weight = ['min 0%'];
    if (crit.weight > 100_000) v.weight = ['max 100%'];
  } else {
    v.weight = ['required'];
  }
  if (!crit.opts && !crit.range) v.scale = ['scale required'];
  if (crit.opts) {
    if (crit.opts.length) {
      let valueCountMap = {};
      crit.opts.forEach((opt) => {
        if (!valueCountMap[opt.value]) valueCountMap[opt.value] = 0;
        valueCountMap[opt.value] += 1;
      });
      const hasDupes = !!Object.values(valueCountMap).find(v => (v > 1));
      if (hasDupes) v.scale = ['each option\'s value must be unique'];
    } else {
      v.scale = ['at least one option required'];
    }
  }
  if (crit.range) {
    if (_.isFinite(crit.range.min) && _.isFinite(crit.range.max)) {
      if (crit.range.min >= crit.range.max) {
        v.rangeMin = ['>= max'];
        v.rangeMax = ['<= min'];
      }
    }
    if (_.isFinite(crit.range.min)) {
      if (crit.range.min < 0) v.rangeMin = ['min 0'];
    } else {
      v.rangeMin = ['required'];
    }
    if (_.isFinite(crit.range.max)) {
      if (crit.range.max < 1) v.rangeMax = ['min 1'];
    } else {
      v.rangeMax = ['required'];
    }
  }
  const invalidOpt = crit.opts && crit.opts.find(o => o.validations);
  if (invalidOpt) v.opts = ['must be valid'];
  return Object.keys(v).length ? v : null;
};

const getVldRub = (rub) => {
  if (!rub) return null;
  const weightsTotal = rub.crits.reduce((wt=0, crit) => { return wt + crit.weight; }, 0);
  const thresholdCountMap = {};
  (rub.gradLbls || []).forEach((gradLbl) => {
    if (!thresholdCountMap[gradLbl.threshold]) thresholdCountMap[gradLbl.threshold] = 0;
    thresholdCountMap[gradLbl.threshold] += 1;    
  });
  const hasDupeThresholds = !!Object.values(thresholdCountMap).find(c => (c > 1));
  const v = {};
  if (weightsTotal !== 100_000) v.weights = ['must total 100%'];
  const invalidCrit = rub.crits.find(c => c.validations);
  if (invalidCrit) v.crits = ['must be valid'];
  if (hasDupeThresholds) v.gradLblThresholds = ['each label\'s min score must be unique'];
  const invalidGradLbl = (rub.gradLbls || []).find(gl => gl.validations);
  if (invalidGradLbl) v.gradLbls = ['must be valid'];
  return Object.keys(v).length ? v : null;
};

const isValid = (rub) => {
  return !getVldRub(rub);
};

const fixId = (id, getUuid) => {
  if (!utils.isUuid(id)) return getUuid();
  return id;
};
const fixIds = (rub, getUuid) => {
  if (rub == null) return null;
  rub.id = fixId(rub.id, getUuid);
  (rub.gradLbls || []).forEach((gl) => {
    gl.id = fixId(gl.id, getUuid);
  });
  (rub.crits || []).forEach((crit) => {
    crit.id = fixId(crit.id, getUuid);
    (crit.opts || []).forEach((opt) => {
      opt.id = fixId(opt.id, getUuid);
    })
  });
  return rub;
};

export default {
  OptionStyles,
  optionOpts,
  scalePresets,
  scalePresetOpts,
  ScalePresetTypes,
  findPresetKey,
  makeRubric,
  makeCrit,
  cloneCrit,
  makeOpt,
  critAttrsFromPreset,
  formatWeight,
  areWeightsEqual,
  fixIds,

  makeRubricPassFail,
  makeRubricScore,
  makeRubricVote,
  makeRubricFinal,

  NormTypes,
  normTypeObjs,
  normTypeOpts,
  GradLabelPresetTypes,
  defaultGradLabelPreset,
  gradLabelPresets,
  gradLabelPresetOpts,
  makeGradLabel,

  getVldGradLbl,
  getVldOpt,
  getVldCrit,
  getVldRub,
  isValid,
};
